import { FC, Fragment, useCallback, useMemo } from 'react';
import { coreCareerApi } from '@cp/entities/career';
import { TagCloud, TagCloudItem } from '@cp/ds/src/components/tagCloud';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { CatalogCareerTagCloudsProps } from './types';

type MutatableItem = Omit<TagCloudItem, 'href' | 'onClick'>;

export const CatalogCareerTagClouds: FC<CatalogCareerTagCloudsProps> = ({ type, sx }) => {
  const routeResolver = useClientWebResolver();
  const { data: careerDictionary, isLoading } = coreCareerApi.endpoints.getCareers.useQuery();
  const hasDictionary = careerDictionary && !isLoading;
  const items = useMemo(
    () =>
      Object.entries({ ...careerDictionary }).map<MutatableItem>(([careerId, careerViewModel]) => ({
        key: careerId,
        label: careerViewModel.translation.namePlural,
      })),
    [careerDictionary],
  );
  const mutateItemHrefs = useCallback(
    (mItems: MutatableItem[], mType: CatalogItemType): TagCloudItem[] =>
      mItems.map<TagCloudItem>((item) => ({
        ...item,
        href: routeResolver
          .resolve({
            page: ClientWebPage.Catalog,
            pageProps: { catalogType: mType, careerId: Number(item.key) },
          })
          .toString(),
      })),
    [routeResolver],
  );

  if (!type) {
    return (
      <Fragment>
        {hasDictionary ? (
          <TagCloud items={mutateItemHrefs(items, CatalogItemType.Vacancy)} sx={sx} title="Вакансии" />
        ) : (
          <ContentSkeleton blocks={3} />
        )}
        {hasDictionary ? (
          <TagCloud items={mutateItemHrefs(items, CatalogItemType.Resume)} sx={sx} title="Резюме" />
        ) : (
          <ContentSkeleton blocks={3} />
        )}
      </Fragment>
    );
  }

  const title = type === CatalogItemType.Resume ? 'Резюме' : 'Вакансии';
  return hasDictionary ? <TagCloud items={mutateItemHrefs(items, type)} sx={sx} title={title} /> : <ContentSkeleton blocks={3} />;
};
